import { EllipsisSpan, fetch, Tag } from '@maxtropy/components';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlarmLevelDisplay } from '@/pages/PvAlarm/types';
import { AlarmLevel, AlarmLevelColorDisplay } from '@/shared/types';
import { useEffect, useState } from 'react';
import {
  V2DeviationRateAlarmRecordPagePostResponse,
  apiV2DeviationRateAlarmRecordGetAlarmObjectPost,
  apiV2DeviationRateAnalysisGetEnergyMediumsPost,
  apiV2DeviationRateAnalysisGetRuleNamesPost,
} from '@maxtropy/device-customer-apis-v2';
import {
  V2EnergyCompareGetMediumIndicatorLossDataPostRequest,
  V2EnergyCompareGetMediumIndicatorLossDataPostResponse,
} from '@maxtropy/device-customer-apis-v2/dist/ytt/apis/v2/energyCompareGetMediumIndicatorLossData';
import { isNil } from 'lodash-es'

// 列表项类型 - 从API响应中提取
export type DeviationAlarmRecordItem = NonNullable<V2DeviationRateAlarmRecordPagePostResponse['list']>[number];

// 报警对象选项类型
export type AlarmObjectOption = {
  label: string;
  value: string; // 格式: `${energyMediumTopologyId}-${topologyNodeId}`
  energyMediumTopologyId: number;
  topologyNodeId: number;
};

// 选项Map类型
export type OptionsMap = {
  alarmObjects?: AlarmObjectOption[];
  energyMediumIds?: Array<{ label: string; value: number }>;
  deviationRateAlarmRuleIds?: Array<{ label: string; value: number }>;
  alarmLevels?: Array<{ label: string; value: number }>;
};

export const useDeviationRecordOptions = () => {
  const [optionsMap, setOptionsMap] = useState<OptionsMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const results = await Promise.allSettled([
          // 报警对象
          apiV2DeviationRateAlarmRecordGetAlarmObjectPost(),
          // 能源介质
          apiV2DeviationRateAnalysisGetEnergyMediumsPost({}),
          // 报警规则
          apiV2DeviationRateAnalysisGetRuleNamesPost(),
        ]);

        // 报警对象选项转换
        const alarmObjects =
          results[0].status === 'fulfilled'
            ? (results[0].value.list || [])
                .filter(item => item.energyMediumTopologyId && item.topologyNodeId)
                .map(item => ({
                  label: item.displayName || '',
                  value: `${item.energyMediumTopologyId}-${item.topologyNodeId}`,
                  energyMediumTopologyId: item.energyMediumTopologyId!,
                  topologyNodeId: item.topologyNodeId!,
                }))
            : [];

        // 能源介质选项转换
        const energyMediumIds = results[1].status === 'fulfilled' ? listToOptions(results[1].value.list) : [];

        // 报警规则选项转换
        const deviationRateAlarmRuleIds = results[2].status === 'fulfilled' ? listToOptions(results[2].value.list) : [];

        setOptionsMap({
          alarmObjects,
          energyMediumIds,
          deviationRateAlarmRuleIds,
          alarmLevels: displayObjToOptions(AlarmLevelDisplay),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  return { loading, optionsMap };
};

// 工具函数：列表数据转为 options
export const listToOptions = <T extends { id?: string | number; name?: string }>(
  list: T[] = []
): Array<{ label: string; value: number }> => {
  return list
    .filter(item => item.id !== undefined && item.name !== undefined)
    .map(item => ({ label: item.name!, value: item.id as number }));
};

// 工具函数：枚举 Display 对象转为 options
export const displayObjToOptions = (enumObj: Record<number, string>) => {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: value,
    value: Number(key),
  }));
};

// 解析报警对象值 (格式: "energyMediumTopologyId-topologyNodeId")
export const parseAlarmObjectValue = (
  value: string
): { energyMediumTopologyId: number; topologyNodeId: number } | null => {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 2) return null;
  return {
    energyMediumTopologyId: Number(parts[0]),
    topologyNodeId: Number(parts[1]),
  };
};

export const getTableListColumns = (): ColumnType<DeviationAlarmRecordItem>[] => [
  {
    title: '报警对象',
    ellipsis: { showTitle: true },
    render: (_: any, record) => {
      const topologyName = record.energyMediumTopologyName;
      const nodeName = record.energyMediumTopologyNodeName;
      if (!topologyName && !nodeName) return '-';
      return <EllipsisSpan value={[topologyName, nodeName].filter(Boolean).join('-')} />;
    },
  },
  {
    title: '能源介质',
    dataIndex: 'energyMediumName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v || '-'} />,
  },
  {
    title: '报警规则',
    dataIndex: 'deviationRateAlarmRuleName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '偏差率',
    dataIndex: 'deviationRate',
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={!isNil(v) ? `${v.toFixed(2)}%` : '-'} />,
  },
  {
    title: '偏差量',
    dataIndex: 'deviationAmount',
    ellipsis: { showTitle: true },
    render: (v: number, record) => {
      const unit = record.physicalUnitName || '';
      return <EllipsisSpan value={!isNil(v) ? `${v.toFixed(2)}${unit}` : '-'} />;
    },
  },
  {
    title: '报警时间',
    dataIndex: 'alarmTime',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'} />,
  },
  {
    title: '报警等级',
    dataIndex: 'alarmLevel',
    ellipsis: { showTitle: true },
    render: (v: AlarmLevel) =>
      v !== undefined && v !== null ? (
        <Tag border="solid" color={AlarmLevelColorDisplay[v]}>
          {AlarmLevelDisplay[v]}
        </Tag>
      ) : (
        '-'
      ),
  },
  {
    title: '统计周期',
    ellipsis: { showTitle: true },
    render: (_: any, record) => {
      if (!record.statCycleFromTime || !record.statCycleToTime) return '-';
      return (
        <EllipsisSpan
          value={`${dayjs(record.statCycleFromTime).format('YYYY-MM-DD HH:mm:ss')} ~ ${dayjs(
            record.statCycleToTime
          ).format('YYYY-MM-DD HH:mm:ss')}`}
        />
      );
    },
  },
];

// 能源介质拓扑统计比较分析 - 按小时(原接口apiV2EnergyCompareGetMediumIndicatorLossDataPost没有按小时, 后端拓展)
export function apiV2EnergyCompareGetMediumIndicatorLossHourlyDataPost(
  params: V2EnergyCompareGetMediumIndicatorLossDataPostRequest
): Promise<V2EnergyCompareGetMediumIndicatorLossDataPostResponse> {
  return fetch(
    `/api/v2/energy/compare/getMediumIndicatorLossData/hourly`,
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
    true
  );
}
